/**
 *  Make tab navigation loop within an element.
 */

// Create attribute selectors to ignore when trying to find tabbables.
/** @type {string} */
const nonTabbables = [
    '[aria-hidden="true"]',
    '[disabled]',
    '[hidden]',
    '[type="hidden"]',
    '[tabindex="-1"]'
]
    .map(selector => `:not(${selector})`)
    .join('');

// Select elements that are by default tabbable when tab navigating and
// append the nonTabbables selectors to each.
/** @type {string} */
const tabbables = [
    `button`,
    `[role="button"]`,
    `a[href]`,
    `input`,
    `[contentEditable=true]`,
    `select`,
    `textarea`,
    `iframe`,
    `[tabindex="0"]`,
    `audio`,
    `video`
]
    .map(selector => `${selector}${nonTabbables}`)
    .join(',');

class TabTrapper {
    /**
     * @param {HTMLElement} element
     */
    constructor(element) {
        /** @type {Array<HTMLElement>} */
        this.tabbables = [];

        /** @type {HTMLElement} */
        this.element = element;

        /** @type {HTMLElement} */
        this.innerDocBody = null;

        this.element.addEventListener('keydown', this.maybeLoopFocus.bind(this));
    }

    teardown() {
        this.element.removeEventListener('keydown', this.maybeLoopFocus);

        if (this.innerDocBody) {
            this.innerDocBody.removeEventListener('keydown', this.maybeLoopFocus.bind(this));
        }
    }

    /**
     * Reselects tabbable elements. This function should be called on each
     * update of element contents.
     */
    init() {
        /** @type {NodeListOf<Element>} */
        const elements = this.element.querySelectorAll(tabbables);

        /** @type {Array<HTMLElement>} */
        const visibleTabbables = removeInvisibles(elements);

        /** @type {HTMLElement} */
        this.firstTabbable = visibleTabbables[0];

        /** @type {HTMLElement} */
        this.lastTabbable = visibleTabbables[visibleTabbables.length - 1];

        /** @type {Array<HTMLElement>} */
        this.tabbableElements = visibleTabbables;
    }

    /**
     * Advance focus to next tabbable element if the event is a TAB key press
     *
     * @param {KeyboardEvent} event
     */
    maybeLoopFocus(event) {
        if (event.key !== 'Tab') {
            return;
        }

        if (!event.shiftKey && event.target === this.lastTabbable) {
            event.preventDefault();
            if (this.firstTabbable.nodeName === 'INPUT' && this.firstTabbable.type === 'radio') {
                const selectedElement = document.querySelector(`input[name="${this.firstTabbable.name}"]:checked`);
                selectedElement
                    ? selectedElement.focus()
                    : this.firstTabbable.focus();
            } else {
                this.firstTabbable.focus();
            }
        }

        if (event.shiftKey && (event.target === this.firstTabbable || isInSameRadioGroup(event.target, this.firstTabbable))) {
            event.preventDefault();
            this.lastTabbable.focus();
        }
    }

    /**
     * Update last tabbable if the last element is an iframe
     *
     * @param {HTMLElement} innerDocBody document body of inner iframe
     */
    updateLastTabbableIfIframeIsTheLastElement(innerDocBody) {
        this.innerDocBody = innerDocBody;

        const elements = innerDocBody.querySelectorAll(tabbables);
        const visibleTabbables = removeInvisibles(elements);

        if (visibleTabbables.length === 0) {
            this.lastTabbable = innerDocBody;
        } else {
            this.lastTabbable = visibleTabbables[visibleTabbables.length - 1];
        }

        innerDocBody.addEventListener('keydown', this.maybeLoopFocus.bind(this));
    }
}

export default TabTrapper;

/**
 * This may have an issue with screen reader only elements, it needs to be tested.
 *
 * @param {NodeListOf<Element>} elements
 * @return {Array<HTMLElement>} All elements that have computed style of visisble.
 */
function removeInvisibles(elements) {
    return [].filter.call(elements, element => {
        return element.style.visibility !== 'hidden' &&
            element.style.display !== 'none';
    });
}

function isInSameRadioGroup(element1, element2) {
    return element1.nodeName === 'INPUT' && element1.type === 'radio' &&
        element2.nodeName === 'INPUT' && element2.type === 'radio' && element1.name === element2.name;
}
